import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import posthog from './posthogClient';
import { supabase } from './supabaseClient';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { UserProvider } from './UserContext';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LandingPage from './components/LandingPage';
import HomeGate from './components/HomeGate';
import BasketContent from './components/BasketContent';
import AuthPage from './components/Auth/AuthPage';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import ConnectionsPage from './components/Connections/ConnectionsPage';
import TopicDetail from './components/TopicDetail/TopicDetail';
import { useDispatch } from 'react-redux';
import { setTopics } from './store/topicsSlice';
import ProtectedRoute from './components/ProtectedRoute';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AcceptBasketPage from './components/AcceptBasket/AcceptBasketPage';

const PosthogPageviews = () => {
  const location = useLocation();

  useEffect(() => {
    if (!process.env.REACT_APP_POSTHOG_KEY) return;
    posthog.capture('$pageview');
  }, [location]);

  return null;
};

function AppContent() {
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const { data, error } = await supabase
          .from('topics')
          .select(`
            id,
            name,
            workspace_id,
            basket_id,
            tag:tags(id, name, color)
          `)
          .limit(100);
        
        if (error) throw error;
        
        dispatch(setTopics(data));
      } catch (error) {
        console.error('Error fetching topics:', error);
      }
    };

    fetchTopics();
  }, [dispatch]);

  return (
    <Router>
      <PosthogPageviews />
      <div className="App">
        <ToastContainer position="top-right" autoClose={3000} />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/workspace" element={
            <ProtectedRoute>
              <HomeGate />
            </ProtectedRoute>
          } />
          <Route path="/basket/:id" element={
            <ProtectedRoute>
              <BasketContent />
            </ProtectedRoute>
          } />
          <Route path="/accept-basket" element={
            <ProtectedRoute>
              <AcceptBasketPage />
            </ProtectedRoute>
          } />
          <Route path="/workspace/:workspaceId" element={
            <ProtectedRoute>
              <ConnectionsPage />
            </ProtectedRoute>
          } />
          <Route path="/topic-detail/:topicId" element={
            <ProtectedRoute>
              <TopicDetail />
            </ProtectedRoute>
          } />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <SessionContextProvider supabaseClient={supabase}>
          <UserProvider>
            <AppContent />
          </UserProvider>
        </SessionContextProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
